import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, MapPinIcon, MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { UniversityListing } from '../types/listings';
interface UniversityListingCardProps {
  listing: UniversityListing;
}

/**
 * A single advertiser listing on a guide page, built to read as a comparison
 * result rather than a display ad.
 *
 * Attention order is deliberate: school name, then the value-proposition
 * headline, then the three stats, then the CTA. Accreditation and location are
 * present for trust but deliberately quiet so they never outweigh the reason to
 * click.
 *
 * On narrow screens the logo and qualifier badge move to a strip across the top
 * so the school is identifiable before any scrolling, and the CTA spans the full
 * width with a tap-down state. The desktop two-column layout is unchanged.
 */
export function UniversityListingCard({
  listing
}: UniversityListingCardProps) {
  const {
    rank,
    schoolName,
    location,
    accreditation,
    topBadge,
    headline,
    benefits,
    logo,
    ctaText,
    ctaUrl,
    ctaSubtext,
    featured
  } = listing;
  const isInternal = ctaUrl.startsWith('/');
  const ctaClasses = 'inline-flex items-center justify-center text-center w-full min-h-[56px] lg:min-h-[60px] px-5 py-4 text-base font-bold rounded-xl text-white bg-cta shadow-md hover:bg-cta-dark hover:shadow-lg lg:hover:-translate-y-0.5 active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-dark transition-[background-color,box-shadow,transform] duration-200 ease-out';
  return <article className="relative" data-id="element-837" data-mp="3kj0jllnrfsl0">
      {featured && <div className="rounded-t-xl px-4 py-2.5 sm:py-3 flex items-center justify-center gap-2 bg-navy" data-id="element-838" data-mp="3oj0xn56y61rj">
          <TrophyIcon className="w-4 h-4 flex-shrink-0 text-gold-light" aria-hidden="true" data-id="element-839" data-mp="1q3y0q6t7v8r2" />
          <span className="text-white text-xs sm:text-sm font-bold tracking-wide" data-id="element-840" data-mp="1u6b1shx0qihi">
            #1 Recommended School
          </span>
        </div>}

      <div className={`bg-white transition-shadow duration-200 ease-out ${featured ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg hover:shadow-xl' : 'rounded-xl border border-gray-200 shadow-sm hover:shadow-md'}`} data-id="element-841" data-mp="2sh2k8xktsrtl">
        {/* Mobile identity block — rank and qualifier badge, then the logo at
            full width. The logo is what makes a school recognizable, so it
            leads; the desktop layout keeps both in the right-hand column. */}
        <div className="lg:hidden px-4 sm:px-6 pt-4 pb-3 border-b border-gray-100" data-id="element-842" data-mp="3q5clvbgp9dk6">
          <div className="flex items-center justify-between gap-3 min-h-[28px]" data-id="element-843" data-mp="3l601qyh7qkla">
            <span className={`inline-flex items-center justify-center flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${featured ? 'bg-navy text-white' : 'bg-light border border-gray-200 text-navy'}`} data-id="element-844" data-mp="3l8459fb79hl1">
              #{rank}
              <span className="sr-only" data-id="element-845" data-mp="mmgi2qeriqfl"> ranked school</span>
            </span>
            {topBadge && <span className="flex-shrink-0 inline-flex items-center text-right bg-light border border-gray-200 text-navy text-[11px] font-semibold leading-tight px-2.5 py-1 rounded-full" data-id="element-846" data-mp="8mx95ve4nb28">
                {topBadge}
              </span>}
          </div>

          <div className="mt-3 h-16 flex items-center justify-center" data-id="element-847" data-mp="3n2j1u26epi8">
            <img src={logo} alt="" aria-hidden="true" className="max-h-full max-w-[200px] object-contain" loading="lazy" data-id="element-848" data-mp="3ododyb9s5bg" />
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:gap-8" data-id="element-849" data-mp="3c34ee6c6xgd4">
          {/* Main content */}
          <div className="flex-1 min-w-0 flex gap-3 sm:gap-4" data-id="element-850" data-mp="3h2h0upz5glf6">
            {/* Rank appears in the mobile block above, so it is desktop-only here. */}
            <div className="hidden lg:block flex-shrink-0 pt-0.5" data-id="element-851" data-mp="390hq0cab5nmo">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${featured ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-852" data-mp="3437wakfvypt1">
                <span className="text-sm font-bold" data-id="element-853" data-mp="2qbb6in1owrzt">{rank}</span>
                <span className="sr-only" data-id="element-854" data-mp="2q8p22wa3islt">Ranked number {rank}</span>
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-855" data-mp="2le1i9aq1emdk">
              {/* Identity */}
              <h3 className="text-lg sm:text-2xl font-bold text-navy leading-tight" data-id="element-856" data-mp="2gcvr3hc0z765">
                {schoolName}
              </h3>

              <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-gray-500" data-id="element-857" data-mp="el8y5a8kp271">
                <span className="inline-flex items-center gap-1.5" data-id="element-858" data-mp="ajds5sfp60mx">
                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" data-id="element-859" data-mp="1shaa1hfj4ppw" />
                  {location}
                </span>
                <span className="inline-flex items-center gap-1.5" data-id="element-860" data-mp="22722a505p6r7">
                  <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-861" data-mp="227vc5euewcdk" />
                  {accreditation}
                </span>
              </div>

              {/* The reason to click */}
              <p className="mt-3 sm:mt-4 text-base sm:text-lg font-bold text-crimson leading-snug" data-id="element-862" data-mp="1ei12151h91x0">
                {headline}
              </p>

              {/* Benefits */}
              <ul className="mt-3 sm:mt-4 space-y-2 sm:space-y-2.5" data-id="element-863" data-mp="1nj8o8oqpycaq">
                {benefits.map(benefit => <li key={benefit} className="flex items-start gap-2.5" data-id="element-864" data-mp="2txgezxwfod1a">
                    <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-865" data-mp="2ywjhpboxy763" />
                    <span className="text-[13px] sm:text-sm text-gray-700 leading-relaxed" data-id="element-866" data-mp="uthuyktxdhm4">
                      {benefit}
                    </span>
                  </li>)}
              </ul>

            </div>
          </div>

          {/* Logo + CTA. On mobile only the CTA and its reassurance line show
              here; the logo and badge already appeared in the strip above. */}
          <div className="mt-5 pt-5 border-t border-gray-100 lg:mt-0 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 lg:w-60 lg:flex-shrink-0 flex flex-col items-center justify-center gap-3 lg:gap-4" data-id="element-867" data-mp="b5sdb6fpcxqm">
            {topBadge && <span className="hidden lg:inline-flex items-center justify-center text-center bg-light border border-gray-200 text-navy text-xs font-semibold leading-tight px-3 py-1.5 rounded-full" data-id="element-868" data-mp="l3eslu0wyrb4">
                {topBadge}
              </span>}

            <div className="hidden lg:flex w-full h-20 items-center justify-center" data-id="element-869" data-mp="3oikk1sqktw0l">
              <img src={logo} alt={`${schoolName} logo`} className="max-w-[200px] max-h-full object-contain" loading="lazy" data-id="element-870" data-mp="2409u9hakt7px" />
            </div>

            {isInternal ? <Link to={ctaUrl} className={ctaClasses} data-id="element-871" data-mp="1z2hygc6t91gg">
                {ctaText}
              </Link> : <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className={ctaClasses} data-id="element-872" data-mp="2mtdp35zj5qm4">
                {ctaText}
              </a>}

            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-873" data-mp="2duigxocxe6g6">
              <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-874" data-mp="2itdanlcflff2" />
              {ctaSubtext}
            </p>
          </div>
        </div>
      </div>
    </article>;
}